/**
 * `useToolbarHost` — locates a DOM element by id and returns it once it
 * exists, so PlateEditor can portal its toolbar into a host slot owned
 * by the parent layout (typically a fixed strip above the editor canvas).
 *
 * Why polling instead of plain `document.getElementById` once:
 *   The parent layout (e.g. DealSummaryEditorPlate) often renders the
 *   toolbar-host `<div id="…">` AND the `<PlateEditor>` in the same
 *   tree, so on first paint PlateEditor's effect may run before React
 *   has flushed the parent's host div to the DOM. A single
 *   `getElementById` call would return null and the toolbar would
 *   never appear. We retry on requestAnimationFrame up to 16 frames
 *   (~250 ms at 60 fps) to ride out the mount-order race.
 *
 * Once found, the host is cached in state and re-emitted via setHost
 * exactly once — no re-polling on prop change unless the id itself
 * changes. Re-running the effect when the id changes also handles
 * the case where a consumer dynamically swaps host slots.
 */
import { useEffect, useState } from 'react';

export function useToolbarHost(toolbarHostId?: string): HTMLElement | null {
    const [host, setHost] = useState<HTMLElement | null>(null);
    useEffect(() => {
        if (!toolbarHostId) {
            setHost(null);
            return;
        }
        // Fast path: host is already in the DOM by the time the effect runs.
        const immediate = document.getElementById(toolbarHostId);
        if (immediate) {
            setHost(immediate);
            return;
        }
        // Slow path: poll on RAF until the host appears or we give up.
        let cancelled = false;
        let tries = 0;
        const tick = () => {
            if (cancelled) return;
            const el = document.getElementById(toolbarHostId);
            if (el) {
                setHost(el);
                return;
            }
            tries += 1;
            // 16 frames ≈ 250 ms at 60 fps — long enough for normal mount
            // ordering, short enough that a genuinely-missing host doesn't
            // burn CPU forever.
            if (tries < 16) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
        return () => {
            cancelled = true;
        };
    }, [toolbarHostId]);
    return host;
}
